import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchCommsPlans, createCommsPlan, updateCommsPlan, deleteCommsPlan } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, Plus, Trash2, Calendar, UserPlus } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { key: "Corpo", label: "Perfil Corporativo" },
  { key: "Personal", label: "Perfil Personal" },
  { key: "Blog LI", label: "Blog LinkedIn" },
  { key: "News LI", label: "Newsletter LinkedIn" },
  { key: "Blog", label: "Blog web" },
  { key: "News L", label: "Newsletter email marketing" },
  { key: "Lead Magnet", label: "Lead Magnets" },
  { key: "Community", label: "Community" },
  { key: "YouTube", label: "YouTube" },
];

// Monthly engagement-metric ranges configured at the plan level.
const ENG_METRICS = [
  { key: "linkedin_impressions", label: "Impresiones LinkedIn / mes", unit: "" },
  { key: "interactions", label: "Interacciones / mes", unit: "" },
  { key: "clicks", label: "Clicks a web/perfil / mes", unit: "" },
  { key: "followers_growth", label: "Nuevos seguidores / mes", unit: "" },
  { key: "newsletter_subs", label: "Suscriptores newsletter LinkedIn / mes", unit: "" },
];

const CADENCES = [
  { value: "monthly", label: "Mensual" },
  { value: "quarterly", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
];

const blank = () => ({
  name: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  cadence: "monthly",
  targets_mode: "total",
  total_targets: { Corpo: 0, Personal: 0, "News L": 0, Blog: 0, "Lead Magnet": 0, Community: 0, YouTube: 0, "Blog LI": 0, "News LI": 0 },
  monthly_commitments: { Corpo: 0, Personal: 0, "News L": 0, Blog: 0, "Lead Magnet": 0, Community: 0, YouTube: 0, "Blog LI": 0, "News LI": 0 },
  engagement_targets: {
    reach_min: 0,
    engagement_rate_min: 0,
    linkedin_impressions_min: 0, linkedin_impressions_max: 0,
    interactions_min: 0, interactions_max: 0,
    clicks_min: 0, clicks_max: 0,
    followers_growth_min: 0, followers_growth_max: 0,
    newsletter_subs_min: 0, newsletter_subs_max: 0,
  },
});

// Compute total months in a plan's date range (rounding up partial months).
const monthsInRange = (startISO, endISO) => {
  if (!startISO) return 0;
  const start = new Date(startISO);
  const end = endISO ? new Date(endISO) : new Date();
  if (start > end) return 0;
  return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
};

// When opening a legacy plan that only has monthly_commitments, derive totals from monthly × months.
const planToEditing = (p) => {
  const hasTotal = p.total_targets && Object.values(p.total_targets).some((v) => Number(v) > 0);
  let total_targets = { Corpo: 0, Personal: 0, "News L": 0, Blog: 0, "Lead Magnet": 0, Community: 0, YouTube: 0, "Blog LI": 0, "News LI": 0 };
  if (hasTotal) {
    total_targets = { ...total_targets, ...p.total_targets };
  } else if (p.monthly_commitments) {
    const m = monthsInRange(p.start_date, p.end_date);
    Object.keys(p.monthly_commitments).forEach((k) => {
      total_targets[k] = Number(p.monthly_commitments[k] || 0) * m;
    });
  }
  // For extras: legacy stored `target` as per-month, new stores as total.
  const extras = (p.engagement_targets?.extra_personal_profiles || []).map((prof) => {
    if (hasTotal) return { name: prof.name || "", target: Number(prof.target || 0) };
    const m = monthsInRange(p.start_date, p.end_date);
    return { name: prof.name || "", target: Number(prof.target || 0) * m };
  });
  // Monthly view of the targets: use stored monthly_commitments or derive.
  const m0 = monthsInRange(p.start_date, p.end_date) || 1;
  const monthly_commitments = { Corpo: 0, Personal: 0, "News L": 0, Blog: 0, "Lead Magnet": 0, Community: 0, YouTube: 0, "Blog LI": 0, "News LI": 0 };
  Object.keys(monthly_commitments).forEach((k) => {
    monthly_commitments[k] = Number(p.monthly_commitments?.[k] || 0) || Math.round(Number(total_targets[k] || 0) / m0);
  });
  return { ...p, total_targets, monthly_commitments, extra_personal_profiles: extras, targets_mode: p.targets_mode || "total" };
};

export default function CommsSettingsDialog({ clientId }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // plan being edited, or "new" form

  const { data: plans = [] } = useQuery({
    queryKey: ["comms-plans", clientId],
    queryFn: () => fetchCommsPlans(clientId),
    enabled: open,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["comms-plans", clientId] });
    qc.invalidateQueries({ queryKey: ["client-quarterly", clientId] });
    qc.invalidateQueries({ queryKey: ["client-communications", clientId] });
    qc.invalidateQueries({ queryKey: ["compliance", clientId] });
  };

  const startNewPlan = () => {
    const next = blank();
    next.name = `Plan ${plans.length + 1}`;
    setEditing(next);
  };

  const createMut = useMutation({
    mutationFn: (payload) => createCommsPlan(clientId, payload),
    onSuccess: () => { toast.success("Plan creado"); setEditing(null); invalidate(); },
    onError: (e) => toast.error("Error: " + (e.response?.data?.detail || e.message)),
  });
  const updateMut = useMutation({
    mutationFn: ({ planId, payload }) => updateCommsPlan(clientId, planId, payload),
    onSuccess: () => { toast.success("Plan actualizado"); setEditing(null); invalidate(); },
    onError: (e) => toast.error("Error: " + (e.response?.data?.detail || e.message)),
  });
  const deleteMut = useMutation({
    mutationFn: (planId) => deleteCommsPlan(clientId, planId),
    onSuccess: () => { toast.success("Plan eliminado"); invalidate(); },
  });

  const handleSave = () => {
    const totalMonths = monthsInRange(editing.start_date, editing.end_date);
    const isMonthly = (editing.targets_mode || "total") === "monthly";
    const payload = {
      name: editing.name,
      start_date: editing.start_date,
      end_date: editing.end_date || null,
      cadence: editing.cadence,
      targets_mode: editing.targets_mode || "total",
      // Whichever mode the user typed in, derive the other so every view works.
      total_targets: isMonthly
        ? Object.fromEntries(Object.entries(editing.monthly_commitments || {}).map(([k, v]) => [k, Math.round(Number(v || 0) * (totalMonths || 1))]))
        : editing.total_targets,
      monthly_commitments: isMonthly
        ? editing.monthly_commitments
        : (totalMonths > 0
            ? Object.fromEntries(Object.entries(editing.total_targets).map(([k, v]) => [k, Math.round(Number(v || 0) / totalMonths)]))
            : {}),
      engagement_targets: {
        ...editing.engagement_targets,
        extra_personal_profiles: editing.extra_personal_profiles || [],
      },
    };
    if (editing.id) updateMut.mutate({ planId: editing.id, payload });
    else createMut.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline" size="sm"
          data-testid="comms-settings-btn"
          className="h-8 rounded-md border-slate-200 text-slate-700 hover:bg-slate-50"
        >
          <Settings className="h-3.5 w-3.5 mr-1.5" /> Configurar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Configuración del módulo</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Definí planes con sus objetivos <span className="font-semibold text-slate-700">totales</span> (no por mes). Si hubo upsellings, creá un Plan 2 desde la fecha del cambio.
          </DialogDescription>
        </DialogHeader>

        {!editing && (
          <div className="space-y-3">
            {plans.length === 0 ? (
              <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-6 text-center">
                <p className="text-sm text-slate-600 mb-3">Todavía no hay planes configurados. Creá uno para definir los objetivos totales del cliente.</p>
              </div>
            ) : (
              plans.map((p) => (
                <div key={p.id} data-testid={`plan-row-${p.plan_number}`} className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold bg-slate-100 px-2 py-0.5 rounded-sm">Plan {p.plan_number}</span>
                      <span className="font-display text-sm font-semibold text-slate-900">{p.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">{CADENCES.find(c => c.value === p.cadence)?.label}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 font-mono">
                      <Calendar className="h-3 w-3 inline mr-1" />
                      {p.start_date} → {p.end_date || "Sin fin"}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      Corp {p.total_targets?.Corpo || (p.monthly_commitments?.Corpo || 0) * monthsInRange(p.start_date, p.end_date)} · Pers {p.total_targets?.Personal || (p.monthly_commitments?.Personal || 0) * monthsInRange(p.start_date, p.end_date)} · News {p.total_targets?.["News L"] || (p.monthly_commitments?.["News L"] || 0) * monthsInRange(p.start_date, p.end_date)} · Blog {p.total_targets?.Blog || (p.monthly_commitments?.Blog || 0) * monthsInRange(p.start_date, p.end_date)} · LM {p.total_targets?.["Lead Magnet"] || (p.monthly_commitments?.["Lead Magnet"] || 0) * monthsInRange(p.start_date, p.end_date)} <span className="text-slate-400">total del plan</span>
                      {p.engagement_targets?.extra_personal_profiles?.length > 0 && (
                        <span className="text-slate-400 ml-1">· +{p.engagement_targets.extra_personal_profiles.length} perfil(es) extra</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => setEditing(planToEditing(p))} data-testid={`edit-plan-${p.plan_number}`}>Editar</Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteMut.mutate(p.id)} className="text-rose-600 hover:bg-rose-50">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
            <Button onClick={startNewPlan} data-testid="new-plan-btn" className="w-full bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Nuevo plan
            </Button>
          </div>
        )}

        {editing && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Nombre del plan</Label>
                <Input placeholder="Ej: Plan inicial, Upselling Q3..." value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="plan-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Desde</Label>
                <Input type="date" value={editing.start_date} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} data-testid="plan-start-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Hasta (opcional)</Label>
                <Input type="date" value={editing.end_date || ""} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} data-testid="plan-end-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Cadencia</Label>
                <Select value={editing.cadence} onValueChange={(v) => setEditing({ ...editing, cadence: v })}>
                  <SelectTrigger data-testid="plan-cadence-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CADENCES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Objetivos de volumen ({(editing.targets_mode || "total") === "monthly" ? "POR MES" : "TOTAL del plan"})
                </Label>
                <div className="inline-flex bg-slate-100 border border-slate-200 rounded-md p-0.5" data-testid="targets-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, targets_mode: "monthly" })}
                    data-testid="targets-mode-monthly"
                    className={`px-2.5 py-1 text-[11px] font-medium rounded-sm transition-colors ${(editing.targets_mode || "total") === "monthly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                  >
                    Por mes
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, targets_mode: "total" })}
                    data-testid="targets-mode-total"
                    className={`px-2.5 py-1 text-[11px] font-medium rounded-sm transition-colors ${(editing.targets_mode || "total") === "total" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                  >
                    Total del período
                  </button>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mb-2 italic">
                {(editing.targets_mode || "total") === "monthly"
                  ? "Cantidad de piezas comprometidas por cada mes del plan."
                  : `Cantidad total de piezas comprometidas para todo el plan (${monthsInRange(editing.start_date, editing.end_date) || "?"} ${monthsInRange(editing.start_date, editing.end_date) === 1 ? "mes" : "meses"}). No por mes.`}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {TYPES.map((t) => (
                  <div key={t.key} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                    <span className="text-xs font-medium text-slate-700">{t.label}</span>
                    <Input
                      type="number" min="0"
                      className="w-20 h-7 text-center"
                      value={(editing.targets_mode || "total") === "monthly"
                        ? (editing.monthly_commitments?.[t.key] ?? 0)
                        : (editing.total_targets?.[t.key] ?? 0)}
                      onChange={(e) => (editing.targets_mode || "total") === "monthly"
                        ? setEditing({ ...editing, monthly_commitments: { ...editing.monthly_commitments, [t.key]: Number(e.target.value) } })
                        : setEditing({ ...editing, total_targets: { ...editing.total_targets, [t.key]: Number(e.target.value) } })}
                      data-testid={`plan-target-${t.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Perfiles personales adicionales</Label>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => setEditing({ ...editing, extra_personal_profiles: [...(editing.extra_personal_profiles || []), { name: "", target: 0 }] })}
                  data-testid="add-extra-personal-btn"
                  className="h-7 text-[11px] border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  <UserPlus className="h-3 w-3 mr-1" /> Agregar perfil
                </Button>
              </div>
              {(editing.extra_personal_profiles || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic bg-slate-50 border border-dashed border-slate-200 rounded-md px-3 py-2.5">
                  Si el cliente tiene más de un perfil personal a gestionar (ej: CEO + CMO), agregalos acá.
                </div>
              ) : (
                <div className="space-y-2">
                  {editing.extra_personal_profiles.map((profile, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`extra-profile-${idx}`}>
                      <Input
                        placeholder="Nombre del perfil (ej: Joaquín Maqueda)"
                        value={profile.name}
                        onChange={(e) => {
                          const next = [...editing.extra_personal_profiles];
                          next[idx] = { ...next[idx], name: e.target.value };
                          setEditing({ ...editing, extra_personal_profiles: next });
                        }}
                        className="flex-1 h-8 text-sm"
                      />
                      <Input
                        type="number" min="0"
                        placeholder="Total / plan"
                        value={profile.target}
                        onChange={(e) => {
                          const next = [...editing.extra_personal_profiles];
                          next[idx] = { ...next[idx], target: Number(e.target.value) };
                          setEditing({ ...editing, extra_personal_profiles: next });
                        }}
                        className="w-24 h-8 text-center"
                      />
                      <Button
                        type="button" size="sm" variant="ghost"
                        onClick={() => {
                          const next = editing.extra_personal_profiles.filter((_, i) => i !== idx);
                          setEditing({ ...editing, extra_personal_profiles: next });
                        }}
                        className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Métricas mensuales a monitorear (rangos esperados)</Label>
              <p className="text-[11px] text-slate-500 mb-2 italic">
                Min y max por mes. Sirven como benchmark al final del plan. Dejá en 0 los que no apliquen.
              </p>
              <div className="grid grid-cols-1 gap-2">
                {ENG_METRICS.map((m) => (
                  <div key={m.key} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2 gap-2">
                    <span className="text-xs font-medium text-slate-700 flex-1 truncate">{m.label}</span>
                    <Input type="number" min="0" placeholder="min" className="w-24 h-7 text-center"
                      value={editing.engagement_targets?.[`${m.key}_min`] ?? 0}
                      onChange={(e) => setEditing({ ...editing, engagement_targets: { ...editing.engagement_targets, [`${m.key}_min`]: Number(e.target.value) } })}
                      data-testid={`plan-${m.key}-min`} />
                    <span className="text-[10px] text-slate-400 font-mono">–</span>
                    <Input type="number" min="0" placeholder="max" className="w-24 h-7 text-center"
                      value={editing.engagement_targets?.[`${m.key}_max`] ?? 0}
                      onChange={(e) => setEditing({ ...editing, engagement_targets: { ...editing.engagement_targets, [`${m.key}_max`]: Number(e.target.value) } })}
                      data-testid={`plan-${m.key}-max`} />
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <div className="flex-1 text-left">
                {(!editing.name || !editing.start_date) && (
                  <div className="text-[11px] text-rose-600" data-testid="plan-validation-msg">
                    {!editing.name && "Falta nombre del plan. "}
                    {!editing.start_date && "Falta fecha desde."}
                  </div>
                )}
              </div>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={!editing.name || !editing.start_date || createMut.isPending || updateMut.isPending} data-testid="plan-save-btn" className="bg-slate-900 hover:bg-slate-800">
                {createMut.isPending || updateMut.isPending ? "Guardando…" : editing.id ? "Guardar cambios" : "Crear plan"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
